import '../list/index.js';
import '../icon/index.js';
import '#lit/components/base/select/index.js';

import {html, nothing} from 'lit';
import {customElement, property, query} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import styles from './style.scss';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Model, SelectedTeam} from './architecture.js';
import {Msg, main} from './architecture.js';
import {getValueByHash} from '#lit/utils/arrays.js';
import type {Constructor} from '#lit/types/classes.js';
import {AvatarColorVariants} from '../avatar/index.js';
import type {CustomInputEvent} from '../input/index.js';
import type {NavEvents} from '#lit/types/events.js';
import {isJourneyRoute} from '#lit/utils/journeys.js';

// Consts
export const SIDENAV_COMPONENT_TAG = 'fly-sidenav';

declare global {
  interface WindowEventHandlersEventMap {
    [NavEvents.TOGGLE_COLLAPSE]: CustomEvent<{collapsed: boolean}>;
  }
}

const ALL_TEAMS: SelectedTeam = {
  name: 'All Teams',
  id: 0,
  organization: {
    name: '',
  },
};

@customElement(SIDENAV_COMPONENT_TAG)
export default class FlySidenav extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @property({type: Boolean, reflect: true})
  collapsed = false;

  @property({type: Boolean})
  collapsible = false;

  @property({type: Boolean})
  displayLogoWithLink = false;

  @property({type: String})
  customLogo = '';

  @property({type: String})
  customIcon = '';

  @property({type: Object})
  selectedOption: SelectedTeam | undefined = undefined;

  @property({type: Array})
  teams: SelectedTeam[] = [];

  @property({type: Boolean, attribute: 'allow_all_teams'})
  allowAllTeams = false;

  @query('.fly-nav')
  flyNav: HTMLElement | undefined;

  static get styles() {
    return [styles];
  }

  get collapseIcon() {
    return this.model.collapsed
      ? 'keyboard_arrow_right'
      : 'keyboard_arrow_left';
  }

  toggleMenu() {
    if (this.flyNav) {
      const style = this.flyNav.style.display;
      if (!style) {
        this.flyNav.style.display = 'grid';
      } else {
        this.flyNav.style.display = '';
      }
    }
  }

  colorVariant(): AvatarColorVariants {
    return getValueByHash<AvatarColorVariants>(
      Object.values(AvatarColorVariants),
      this.selectedOption?.name || 'All Teams'
    );
  }

  renderAnchorContent() {
    if (this.model.collapsed) {
      return html`
        <fly-avatar
          circle
          name="${this.selectedOption?.name || ALL_TEAMS.name}"
          variant="${this.colorVariant()}"
        ></fly-avatar>
      `;
    } else {
      /**
       * We use css to render either the icon
       * or the full selection dropdown depending on screen width
       */
      return html`
        <div class="teams-dropdown__container__icon">
          <fly-avatar
            circle
            name="${this.selectedOption?.name || ALL_TEAMS.name}"
            variant="${this.colorVariant()}"
          ></fly-avatar>
        </div>
        <div class="teams-dropdown__container__full">
          <div class="teams-dropdown__team-name-container">
            ${this.model.selectedOption?.name || ALL_TEAMS.name}
            <fly-icon
              icon="${this.model.isOpen ? 'expand_less' : 'expand_more'}"
            ></fly-icon>
          </div>
          <p class="teams-dropdown__org-name">
            ${this.model.selectedOption
              ? this.model.selectedOption.organization.name
              : ''}
          </p>
        </div>
      `;
    }
  }

  renderDropdownContent() {
    if (!this.model.isOpen) return nothing;
    return html`<div class="team-popover" slot="content">
      <div class="list">
        <fly-input
          hasIcon
          type="Search"
          placeholder="Search teams"
          @input=${(e: CustomEvent<CustomInputEvent>) => {
            this.handleUpdate(Msg.filterTeams(e.detail.value));
          }}
        >
          <div slot="icon">
            <fly-icon icon="search"></fly-icon>
          </div>
        </fly-input>
        <fly-list>
          ${when(
            this.allowAllTeams && !this.model.queryTerm,
            () => {
              return html`<fly-list-item
                .value=${ALL_TEAMS.name}
                @selected=${() => {
                  this.handleUpdate(Msg.teamUpdateRequest(ALL_TEAMS));
                }}
              >
                <div class="team-name all-teams-name">${ALL_TEAMS.name}</div>
              </fly-list-item>`;
            },
            () => nothing
          )}
          ${repeat(
            this.model.filteredTeams,
            team => team.id, // unique identifier
            team => html`
              <fly-list-item
                .value=${team.name}
                @selected=${() => {
                  this.handleUpdate(Msg.teamUpdateRequest(team));
                }}
              >
                <div class="team-name">${team.name}</div>
                <p class="teams-dropdown__org-name">
                  ${team.organization.name}
                </p>
              </fly-list-item>
            `
          )}
          ${when(
            this.model.queryTerm && this.model.filteredTeams.length === 0,
            () => html` <p>No teams found</p> `
          )}
        </fly-list>
      </div>
    </div>`;
  }

  renderDropdown() {
    return html`
      <fly-dropdown-v2
        ?open=${this.model.isOpen}
        optionsPlacement="bottom-start"
        closeWhenClickOutside
        updateOnAnimationFrame
        @open=${() => this.handleUpdate(Msg.isOpen(true))}
        @close=${() => this.handleUpdate(Msg.isOpen(false))}
      >
        <div
          slot="anchor"
          class="${classMap({
            'teams-dropdown__error': !!this.model.error,
          })} button__anchor"
        >
          ${this.renderAnchorContent()}
        </div>
        ${this.renderDropdownContent()}
      </fly-dropdown-v2>
    `;
  }

  renderAnyErrors() {
    let errorMessage: string | unknown = '';
    if (
      typeof this.model.error === 'object' &&
      this.model.error !== null &&
      'message' in this.model.error
    ) {
      errorMessage = this.model.error.message;
    }
    return html`
      <div class="teams-dropdown__error-message">
        <span>${errorMessage}</span>
      </div>
    `;
  }

  renderLogo() {
    if (this.displayLogoWithLink) {
      return html`
        <div
          class="fly-nav__brand--responsive"
          @click="${() => {
            location.href = '/audiences';
          }}"
        >
          <fly-brand
            customLogo=${this.customLogo}
            customIcon=${this.customIcon}
            responsive
          ></fly-brand>
        </div>
      `;
    } else {
      return html`
        <div class="fly__brand">
          <fly-brand
            customLogo=${this.customLogo}
            customIcon=${this.customIcon}
          ></fly-brand>
        </div>
      `;
    }
  }

  renderCollapseIcon() {
    if (isJourneyRoute() || !this.collapsible) return nothing;

    return html`<div
      class="fly-nav__collapse-toggle ${classMap({
        'fly-nav__collapse-toggle__open': !this.model.collapsed,
        'fly-nav__collapse-toggle__closed': !!this.model.collapsed,
      })}"
      @click=${() => this.handleUpdate(Msg.toggleCollapse)}
    >
      <fly-icon icon="${this.collapseIcon}"> </fly-icon>
    </div>`;
  }

  render() {
    return html`
      <div
        @mouseenter=${() => {
          if (isJourneyRoute()) this.handleUpdate(Msg.openNav());
        }}
        @mouseleave=${() => {
          if (isJourneyRoute()) this.handleUpdate(Msg.closeNav());
        }}
        class="fly-nav ${classMap({
          'fly-nav--collapsed': this.model.collapsed,
        })}"
      >
        <div class="fly-nav__menu">
          <div
            class="fly-nav__logo-container ${classMap({
              'fly-nav__logo-container__open': !this.model.collapsed,
              'fly-nav__logo-container__closed': !!this.model.collapsed,
            })}"
          >
            ${this.renderLogo()} ${this.renderCollapseIcon()}
          </div>
          <div class="fly-nav-team-selection">
            ${when(
              this.teams.length > 1,
              () => this.renderDropdown(),
              () => nothing
            )}
            ${when(
              !!this.model.error,
              () => this.renderAnyErrors(),
              () => nothing
            )}
          </div>
          <div class="fly-nav-toggle" @click=${() => this.toggleMenu()}>
            <fly-icon icon="menu"></fly-icon>
          </div>
          <slot></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fly-sidenav': FlySidenav;
  }
}
